// Per spec/components.md §7. Compact pairing of member + chaired event.
// **P-037.16 D-116** — both arms share the same stacked typography:
// member name on line 1 (semibold base), event name + event date stacked
// underneath. The two arms previously differed only by whether the
// `PHOTO_SIZE = 40` round photo cell rendered to the left.
//
// **P-037.17 D-117** — the event line splits into two rows: event name
// (line 2) and event date (line 3), with a 4 px gap matching the
// name→event-line idiom.
//
// **P-037.18 D-118** — optional `memberUrl`: when present, the member
// name renders as a link in the template-level link color (matches the
// member-list D-117 pattern). Default name color (no URL) is the text
// primitive's default (`tokens.color['text-primary']`); link arm uses
// `linkColor` from the consuming Block's `ctx.linkColor`.
//
// **P-037.22 D-130** — photo bumped 40 → 100 px (Banks-confirmed) and
// the with-photo arm restructured from side-by-side to stacked (photo
// on row 1, content stacked below) so the card fits the 190 px 3-col
// cell after the larger avatar. The no-photo arm was unchanged in
// structure (single inner stack table) — only the with-photo arm
// reshaped.
//
// **P-037.25 D-133** — Banks reverts the D-130 stacked decision: the
// with-photo arm is back to side-by-side (avatar LEFT of name + event
// + date stack on the RIGHT) and the photo shrinks 100 → 36 × 36.
//
// **P-037.29 post-Phase-Close §item E** — two corrections to the D-133
// layout:
//
// 1. **Name line-height tightened** (default 1.5 → `tokens.type.leading.
//    tight` = 1.2) so the name's cap-height sits at the cell top
//    (matching the avatar's pixel top). Pre-correction, the 1.5
//    line-height's half-leading pushed the cap ~4 px below the cell
//    top, making the avatar visually appear "higher than the text."
//
// 2. **No-photo arm drops the empty 36×36 cell** (reverts the D-133
//    column-preservation). With no avatar, the content stack renders
//    flush-left (x=0) — Banks's "for records without an avatar image,
//    align to the left." Names no longer align vertically across mixed
//    photo + no-photo grids; explicit trade-off Banks chose.

import { html } from '../render'
import { tokens } from '../tokens'
import { text } from '../primitives/text'
import { image } from '../primitives/image'
import { link } from '../primitives/link'
import { HtmlString } from '../types'
import { secondaryText } from './helpers'

export interface DayChairItemProps {
  memberName: string
  eventName: string
  eventDate?: string
  memberPhotoUrl?: string
  /** Optional URL — wraps memberName in an anchor when present.
   *  P-037.18 D-118. */
  memberUrl?: string
  /** Template-level link color from `RenderContext.linkColor` (P-037.18
   *  D-118). Used as the name anchor color when `memberUrl` is set.
   *  Defaults to `tokens.color['link-default']`. */
  linkColor?: string
}

const PHOTO_SIZE = 36
// 12 px horizontal gutter between the avatar cell and the content stack.
const PHOTO_GUTTER = tokens.space[3]
// 4 px gap matches the name→event-line idiom used elsewhere (EventRow,
// MemberCard tight pairings).
const ROW_GAP = tokens.space[1]

export function dayChairItem({
  memberName,
  eventName,
  eventDate,
  memberPhotoUrl,
  memberUrl,
  linkColor,
}: DayChairItemProps): HtmlString {
  // P-037.18 D-118: name conditional color — text-primary default w/o URL,
  // template-level link color w/ URL (member-list D-117 pattern).
  // P-037.29 post-Phase-Close §item E: `lineHeight: tight (1.2)` (was
  // default 1.5) so the name's cap-height sits at cell-top, aligning
  // visually with the avatar's pixel top.
  const nameLinkColor = linkColor ?? tokens.color['link-default']
  const nameHtml =
    memberUrl !== undefined
      ? text({
          text: link({
            text: memberName,
            href: memberUrl,
            color: nameLinkColor,
            weight: tokens.type.weight.semibold,
          }),
          weight: tokens.type.weight.semibold,
          color: nameLinkColor,
          lineHeight: tokens.type.leading.tight,
        })
      : text({
          text: memberName,
          weight: tokens.type.weight.semibold,
          lineHeight: tokens.type.leading.tight,
        })
  const eventNameHtml = secondaryText(eventName)
  const eventDateHtml = eventDate !== undefined ? secondaryText(eventDate) : undefined
  // prettier-ignore
  const dateRow =
    eventDateHtml !== undefined
      ? html`<tr><td style="padding-top: ${ROW_GAP}px;">${eventDateHtml}</td></tr>`
      : undefined

  // Content stack — name + event + optional date, always emitted.
  // P-037.31 third post-Phase-Close §A — event-name row now carries
  // the same 4 px (`ROW_GAP` = tokens.space[1]) top-pad as the date
  // row so the name → event gap matches the event → date gap (was
  // 0 px → looked too tight vs. the date row's existing 4 px).
  // prettier-ignore
  const contentStack = html`<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td>${nameHtml}</td></tr><tr><td style="padding-top: ${ROW_GAP}px;">${eventNameHtml}</td></tr>${dateRow}</table>`

  // P-037.29 post-Phase-Close §item E — no-photo arm drops the empty
  // avatar cell entirely + content stack renders flush-left. With-photo
  // arm keeps the 2-col `<td>` | `<td>` table (avatar | content). The
  // two arms diverge in row structure now (was: shared 2-col table
  // with the no-photo arm preserving an empty avatar cell per D-133).
  if (memberPhotoUrl === undefined) {
    return contentStack
  }

  const avatarImg = image({
    src: memberPhotoUrl,
    alt: memberName,
    width: PHOTO_SIZE,
    height: PHOTO_SIZE,
    borderRadius: tokens.radius.full,
  })
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td valign="top" width="${PHOTO_SIZE}" style="padding-right: ${PHOTO_GUTTER}px;">${avatarImg}</td><td valign="top">${contentStack}</td></tr></table>`
}
